import React, { useMemo } from "react";
import { isString } from "lodash";
import { Counterparty } from "../../../common/types/counterparty";
import {
    RuleConditionType,
    TransactionRule,
    TransactionRuleCondition,
} from "../../../common/types/transactionRule";
import { getCounterpartyRuleConditionLabel } from "../../../common/helpers/rules";
import { useCategoriesById } from "../../../hooks/useCategoriesById";

interface Props {
    condition: TransactionRuleCondition;
    rule: Pick<TransactionRule, "conditions">;
    counterparties: Counterparty[];
}

export const RuleCounterpartyCondition: React.FC<Props> = ({
    condition,
    rule,
    counterparties,
}) => {
    const categoriesById = useCategoriesById();
    const usedTaxonomy = useMemo(() => {
        const categoryCondition = rule.conditions.find(
            (c) =>
                c.conditionType === RuleConditionType.CATEGORY ||
                c.conditionType === RuleConditionType.SUGGESTED_CATEGORY,
        );

        if (!categoryCondition) {
            return;
        }

        if (isString(categoryCondition.value)) {
            return categoriesById[categoryCondition.value]?.taxonomy;
        }
    }, [rule.conditions, categoriesById]);

    // Handle both single counterparty ID and array of counterparty IDs
    const counterpartyIds = isString(condition.value)
        ? [condition.value]
        : (condition.value as string[]);

    // Find all matching counterparties
    const matchedCounterparties = counterparties.filter((c) =>
        counterpartyIds.includes(c.id),
    );

    if (!matchedCounterparties.length) {
        return null;
    }

    // Get the appropriate label - use the same label for all if they're the same taxonomy
    // or use the general label if there's a mix of taxonomies
    const conditionLabel = getCounterpartyRuleConditionLabel(usedTaxonomy);

    return (
        <>
            {conditionLabel}{" "}
            {matchedCounterparties.length > 1 ? (
                <>
                    is one of{" "}
                    <strong>
                        {matchedCounterparties.map((cp) => cp.name).join(", ")}
                    </strong>
                </>
            ) : (
                <>
                    is <strong>{matchedCounterparties[0].name}</strong>
                </>
            )}
        </>
    );
};
